import {
  Flex,
  Icon,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import UserAvatar from 'react-user-avatar'
import React, { useRef } from 'react'
import {
  DigitalCardFragment,
  DigitalCardOrder,
  DigitalCardOrderDataItem,
  VerifyPhoneNumberResourceType,
} from 'src/graphql/generated/graphql'
import { useFlag, useSelector, useState } from 'src/hooks'
import styles from './actSetupPanel.module.scss'
import ActField from './ActField'
import { formatAmount } from 'src/helpers/payments'
import { CurrentMiniUpsale } from '../CurrentMiniUpsale/CurrentMiniUpsale'
import { Button } from 'src/design_system/molecules/button/Button'
import { SelectableGiftCardType } from '../GiftCard/GiftCard'
import { useFlags } from 'src/react_query'
import { Icon as DSIcon } from 'src/design_system/atoms/icons/components/Icon'
import ActTotal from './ActTotal'
import { PhoneNumberVerificationDialogForAccount } from 'src/phone_number_verification/PhoneNumberVerificationDialogForAccount'
import { AnimatePresence, motion } from 'framer-motion'
import { PhoneNumberVerificationDialogGetActJWT } from 'src/phone_number_verification/PhoneNumberVerificationDialogGetActJWT'

export type SetupActions =
  | 'EditRecipient'
  | 'EditSender'
  | 'EditPaymentMethod'
  | 'AddGiftCard'
  | 'Main'

const INPUT_LABEL_STYLE = { fontSize: '14px', flexShrink: 0 }
const MAGNIFIER = <div className={styles.magnifier} />
const editIcon = <DSIcon name="pencil" size={20} />

type FromNameType = {
  value: string
  onChangeFromName: (value: string) => void
  onFinishEdit: () => void
  defaultPlaceholder?: string
}

const PHONE_VERIFICATION_DIALOG_BG_VARIANTS = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0.6,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.5,
    },
  },
}
const PHONE_VERIFICATION_DIALOG_VARIANTS = {
  enter: {
    opacity: 1,
    y: '0%',
    transition: {
      duration: 0.25,
      type: 'tween',
    },
  },
  exit: {
    opacity: 0,
    y: '100dvh',
  },
}

type ActSetupPanelProps = {
  digitalCard?: DigitalCardFragment
  digitalCardOrder?: DigitalCardOrder | null
  isPreviewOpen: boolean
  title: {
    value: string
    onChangeTitle: (title: string) => void
  }
  fromName: FromNameType
  isLoadingGiftCard?: boolean
  giftCard?: SelectableGiftCardType
  isPaymentAuthenticationOpen: boolean
  onClosePaymentAuthentication: () => void
  onSetPaymentAuthentication: (jwt: string) => void
  onSend: () => void
  onSetupAction: (action: SetupActions) => void
  onTogglePreview: () => void
  errorMsg?: string
}

const ActSetupPanel: React.FC<ActSetupPanelProps> = props => {
  const {
    digitalCard,
    digitalCardOrder,
    isPreviewOpen,
    title,
    fromName,
    isLoadingGiftCard,
    giftCard,
    isPaymentAuthenticationOpen,
    onClosePaymentAuthentication,
    onSetPaymentAuthentication,
    onSend,
    onSetupAction,
    onTogglePreview,
    errorMsg,
  } = props
  const isDesktop = useSelector(state => state.window.width >= 800)
  const account = useSelector(state => state.user.account)
  const { data: flags } = useFlags()
  const { canPurchaseActGiftCards: canPurchaseActGiftCardsFlag } = useFlag(
    flags ?? [],
  )
  const isNotSparse = !!account?.username
  const canPurchaseActGiftCards = canPurchaseActGiftCardsFlag && isNotSparse
  const paymentInfo =
    account?.stripeSource?.__typename === 'CreditCard'
      ? {
          cardType: account.stripeSource.brand,
          last4: account.stripeSource.last4,
          exp: `${account.stripeSource.expMonth}/${account.stripeSource.expYear}`,
        }
      : undefined

  const [shouldEditFromName, setShouldEditFromName] = useState(false)
  const [fromNamePreEdit, setFromNamePreEdit] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const explicitUpdateRef = useRef(false)
  const fromMugShot: string = account?.storefront?.mugshot?.url ?? ''

  const getFormattedTotalPriceWithDiscounts = (
    item?: DigitalCardOrderDataItem,
  ) => {
    const itemBasePrice = item?.amount ?? 0
    const itemTotalDiscount =
      item?.discounts?.reduce((acc, i) => acc + (i ? i.amount : 0), 0) ?? 0
    const itemTotalPrice = itemBasePrice - itemTotalDiscount

    return itemTotalPrice ? formatAmount(itemTotalPrice) : undefined
  }

  const { tax, total } = digitalCardOrder?.data ?? {}

  const formattedActCardTotalPrice = getFormattedTotalPriceWithDiscounts(
    digitalCardOrder?.data.items.find(item => item.sku === '15643'),
  )

  const formattedUpgradeTotalPrice = getFormattedTotalPriceWithDiscounts(
    digitalCardOrder?.data.items.find(item => item.sku === '15657'),
  )

  const giftCardTotal = digitalCardOrder?.data.items
    .filter(item => item.sku === '15661')
    ?.reduce((acc, i) => i.amount, 0)

  const processingFeesTotal = digitalCardOrder?.data.items
    .filter(item => item.sku === '13436')
    ?.reduce((acc, i) => i.amount, 0)

  const formattedGiftCardTotal = giftCardTotal
    ? formatAmount(giftCardTotal)
    : undefined
  const formattedProcessingFeesTotal = processingFeesTotal
    ? formatAmount(processingFeesTotal)
    : undefined
  const formattedTotal = total !== undefined ? formatAmount(total) : '0.00'
  const formattedTaxTotal =
    tax !== undefined && tax > 0 ? formatAmount(tax) : ''
  const hasTax = formattedTaxTotal && formattedTaxTotal !== '0.00'

  const hasRecipient = digitalCard?.recipients[0] !== undefined
  const hasPaymentSource = !!account?.stripeSource
  const isTotalBiggerThanZero = !!(total && total > 0)

  const [shouldShowPhoneVer, setShouldShowPhoneVer] = useState(false)

  const handleAddGiftCard = () => {
    if (!account?.verifiedPhoneNumber) {
      setShouldShowPhoneVer(true)
    } else {
      onSetupAction('AddGiftCard')
    }
  }

  const handleClosePhoneVerification = () => {
    setShouldShowPhoneVer(false)
  }

  const getGiftCardAmountCurrency = (currency: string): string => {
    if (currency.toUpperCase() === 'USD') {
      return ''
    }
    return currency.toLocaleUpperCase()
  }

  const getGiftCardAmountIntegerPart = (amount: number): string => {
    // This won't work for currencies that don't have
    // decimal parts as ToFixed(2) forces the result to have 2 decimal places.
    // When we start supporting more currencies we need to improve this
    // to consider the currency
    const splitedAmount = amount.toFixed(2).split('.')
    return splitedAmount[0]
  }

  const getGiftCardAmountDecimalPart = (amount: number): string => {
    // This won't work for currencies that don't have
    // decimal parts as ToFixed(2) forces the result to have 2 decimal places.
    // When we start supporting more currencies we need to improve this
    // to consider the currency
    const splitedAmount = amount.toFixed(2).split('.')
    if (splitedAmount.length !== 2) {
      return ''
    }
    return `.${splitedAmount[1]}`
  }

  const commitIfChanged = async () => {
    const prev = (fromNamePreEdit ?? '').trim()
    const next = fromName.value.trim()
    if (next === prev) {
      setShouldEditFromName(false)
      return
    }
    await fromName.onFinishEdit()
    setShouldEditFromName(false)
  }

  return (
    <>
      <AnimatePresence>
        {canPurchaseActGiftCards &&
          (shouldShowPhoneVer || isPaymentAuthenticationOpen) && (
            <motion.div
              className={styles.phoneVerification}
              variants={PHONE_VERIFICATION_DIALOG_BG_VARIANTS}
              initial="exit"
              exit="exit"
              animate="enter"
            >
              <motion.div
                className={styles.dialog}
                variants={PHONE_VERIFICATION_DIALOG_VARIANTS}
                initial="exit"
                exit="exit"
                animate="enter"
              >
                <div className={styles.headerArea}>
                  <div
                    className={styles.closeButton}
                    onClick={handleClosePhoneVerification}
                  >
                    <Icon name="close" size={12} primaryColor={'default'} />
                  </div>
                </div>
                {shouldShowPhoneVer && (
                  <PhoneNumberVerificationDialogForAccount
                    onSuccess={success => {
                      if (success) {
                        setShouldShowPhoneVer(false)
                        onSetupAction('AddGiftCard')
                      }
                    }}
                    isCreateOrLogin={false}
                    shouldNotCreateNewAccount={true} // Only adds verified phone number to the account
                    shouldHideCardHeroImg={true}
                    title="Verify your phone before you can add a Gift Card"
                    validateButtonTitle="Verify My Phone"
                  />
                )}
                {isPaymentAuthenticationOpen && (
                  <PhoneNumberVerificationDialogGetActJWT
                    title="We just need to confirm it’s you."
                    resourceType={VerifyPhoneNumberResourceType.ActSender}
                    resourceId={account?.id ?? 'InvalidID'}
                    initialPhoneNumber={
                      account?.verifiedPhoneNumber
                        ? {
                            countryIsoCode:
                              account?.verifiedPhoneNumber?.country.isoCode,
                            nationalNumber:
                              account?.verifiedPhoneNumber?.nationalNumber,
                          }
                        : undefined
                    }
                    onClose={onClosePaymentAuthentication}
                    onSuccess={(jwt: string) => {
                      // Save JWT
                      onSetPaymentAuthentication(jwt)
                    }}
                  />
                )}
              </motion.div>
            </motion.div>
          )}
      </AnimatePresence>
      <div
        className={`${styles.wrapper} ${!isDesktop ? styles.mobileWrapper : ''}`}
      >
        <div
          className={`${styles.header} ${!isDesktop ? styles.mobileHeader : ''}`}
        >
          {isPreviewOpen ? (
            <>
              <Button
                padding="12px"
                borderRadius="0.5rem"
                fill="#404040"
                onClick={onTogglePreview}
                isFullWidth={true}
              >
                <Text
                  type="footnote"
                  content="Continue Sending Act"
                  style={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#fff',
                  }}
                />
              </Button>
            </>
          ) : (
            <>
              <Text
                type="body"
                content="Who would you like to send it to"
                weight="extraBold"
                alignment="center"
                style={{ fontSize: '18px' }}
              />
            </>
          )}
        </div>
        <div className={styles.setup}>
          <div className={styles.row}>
            <Text
              type="body"
              content="Title:"
              weight="bold"
              style={INPUT_LABEL_STYLE}
            />
            <input
              placeholder={title.value}
              className={styles.actSetupInput}
              value={title.value}
              onChange={e => {
                title.onChangeTitle(e.target.value)
              }}
            />
          </div>
          <div className={styles.row}>
            <Text
              type="body"
              content="To:"
              weight="bold"
              style={INPUT_LABEL_STYLE}
            />
            <ActField
              placeHolder="Search or Add a Recipient"
              rigthElement={MAGNIFIER}
              onClick={() => onSetupAction('EditRecipient')}
              filledInfo={
                digitalCard?.recipients[0] !== undefined ? (
                  <>
                    <div className={styles.selectedItem}>
                      <Text
                        type="body"
                        weight="semiBold"
                      >{`${digitalCard.recipients[0].contactRequest?.contact?.firstName ?? digitalCard.recipients[0].contactRequest?.firstName ?? ''} ${digitalCard.recipients[0].contactRequest?.contact?.lastName ?? digitalCard.recipients[0].contactRequest?.lastName ?? ''}`}</Text>
                    </div>
                  </>
                ) : undefined
              }
            />
          </div>
          {!!fromName.defaultPlaceholder && (
            <div className={styles.row}>
              <Text
                type="body"
                content="From:"
                weight="bold"
                style={INPUT_LABEL_STYLE}
              />
              {shouldEditFromName ? (
                <Flex width="100%" alignItems="center">
                  <input
                    ref={inputRef}
                    type="text"
                    className={styles.actSetupInput}
                    value={fromName.value}
                    onChange={e => fromName.onChangeFromName(e.target.value)}
                    onBlur={() => {
                      if (explicitUpdateRef.current) {
                        explicitUpdateRef.current = false
                        return
                      }
                      void commitIfChanged()
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        explicitUpdateRef.current = true
                        void commitIfChanged()
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        fromName.onChangeFromName(fromNamePreEdit ?? '')
                        setShouldEditFromName(false)
                      }
                    }}
                    autoCorrect="off"
                    spellCheck="false"
                    autoComplete="off"
                    maxLength={100}
                  />
                  <div
                    onMouseDown={() => {
                      explicitUpdateRef.current = true
                    }}
                    onClick={() => {
                      void commitIfChanged()
                    }}
                    style={{ marginLeft: '8px', cursor: 'pointer' }}
                  >
                    {editIcon}
                  </div>
                </Flex>
              ) : (
                <ActField
                  placeHolder={fromName.value}
                  onClick={() => {
                    setFromNamePreEdit(fromName.value) // snapshot here
                    setShouldEditFromName(true)
                    setTimeout(() => inputRef.current?.focus(), 0)
                  }}
                  rigthElement={editIcon}
                  filledInfo={
                    <div className={styles.selectedItemWithAvatar}>
                      <UserAvatar
                        name={fromName.value}
                        src={fromMugShot}
                        size={21}
                      />
                      <Text type="body" weight="semiBold">
                        {fromName.value}
                      </Text>
                    </div>
                  }
                />
              )}
            </div>
          )}
          {isTotalBiggerThanZero && (
            <div className={styles.row}>
              <Text
                type="body"
                content="Payment Method:"
                weight="bold"
                style={INPUT_LABEL_STYLE}
              />
              <ActField
                placeHolder="Add Payment Method"
                rigthElement={
                  <Icon
                    primaryColor="primaryBodyText"
                    name="creditCard"
                    size={24}
                  />
                }
                onClick={() => onSetupAction('EditPaymentMethod')}
                filledInfo={
                  paymentInfo ? (
                    <div className={styles.selectedItemWithAvatar}>
                      <Text type="body" weight="semiBold">
                        Ending {paymentInfo?.last4}
                      </Text>
                    </div>
                  ) : undefined
                }
              />
            </div>
          )}
        </div>
        {canPurchaseActGiftCards && (
          <div className={styles.giftCard}>
            {giftCard && !isLoadingGiftCard ? (
              <div
                className={styles.giftCardSelection}
                onClick={handleAddGiftCard}
              >
                <div className={styles.giftCardItem}>
                  <div
                    className={styles.giftCardImgUrl}
                    style={{ backgroundImage: `url('${giftCard.imageUrl}')` }}
                  />
                  <Text
                    type="caption"
                    content="Gift Card: "
                    style={{ flexShrink: 0 }}
                  />
                  <Text
                    type="caption"
                    content={giftCard.title}
                    weight="semiBold"
                    style={{
                      color: '#404040',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'pre',
                      overflow: 'hidden',
                    }}
                  />
                </div>
                <div className={styles.giftCardPrice}>
                  <Text
                    type="body"
                    content={`${getGiftCardAmountCurrency(giftCard.currency)}`}
                    weight="bold"
                    style={{ color: '#404040' }}
                  />
                  <Text
                    type="body"
                    content={`$${getGiftCardAmountIntegerPart(giftCard.amount)}`}
                    weight="bold"
                    style={{ color: '#404040' }}
                  />
                  <Text
                    type="footnote"
                    content={`${getGiftCardAmountDecimalPart(giftCard.amount)}`}
                    weight="bold"
                    style={{ color: '#404040' }}
                  />
                </div>
              </div>
            ) : (
              <>
                {isLoadingGiftCard ? (
                  <div className={styles.loadingArea}>
                    <LoadingSpinner size="small" />
                  </div>
                ) : (
                  <div
                    className={styles.addGiftCardButton}
                    onClick={handleAddGiftCard}
                  >
                    <Text
                      type="body"
                      color="primaryHeading"
                      content="Add a Gift Card"
                      weight="semiBold"
                    />
                    <DSIcon name="gift_card_empty" size={30} fill="#202020" />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <div className={styles.checkoutInfo}>
          <ActTotal
            actCardTotal={formattedActCardTotalPrice}
            premiumDesignTotal={formattedUpgradeTotalPrice}
            giftCardTotal={formattedGiftCardTotal}
            processingFeeTotal={formattedProcessingFeesTotal}
            taxesTotal={hasTax ? formattedTaxTotal : undefined}
            total={formattedTotal}
          />
        </div>
        <div className={styles.upsell}>
          <CurrentMiniUpsale size="Xsmall" />
        </div>
        {errorMsg && (
          <div
            style={{
              padding: '0px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Icon name="exclamation" primaryColor={'danger'} size="large" />
            <Text color="danger" type="body">
              {errorMsg}
            </Text>
          </div>
        )}
        <div className={styles.actionButton}>
          <Button
            padding="8px 12px"
            isFullWidth={true}
            borderRadius="0.75rem"
            fill="#404040"
            isDisabled={
              !hasRecipient || (!hasPaymentSource && isTotalBiggerThanZero)
            }
            onClick={onSend}
          >
            <Text
              type="body"
              content="Send"
              style={{
                fontSize: '1rem',
                fontWeight: 700,
                color: '#fff',
              }}
            />
          </Button>
        </div>
      </div>
    </>
  )
}

export default ActSetupPanel
